/* eslint-disable react/jsx-props-no-spreading */
import { AppContext, getToken, usePermission } from "@ctsu/loader";
import { Header, Modal } from "@ctsu/react-ctsu-ui";
import csmKeys from "assets/text/csmKeys";
import { text } from "components/common/Text";
import { useGetOneOfAdminPermissions, useGetUserGroup } from "helpers/getAppConfig";
import { GetDecodedQueryParams } from "helpers/utils";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import appRoutes from "routes/appRoutes";
import { useFetchRosterOwnerLovQuery } from "stores/apis/apiSlice";
import { updateUserGroup } from "stores/redux/actions/person";
import { useUserAffliations } from "../../helpers/getAppConfig";

const cogGroup = ["COG", "PEP-CTN"];

function LogoutCmp() {
  return <NavLink to="/logout">{text("header.nav_bar.logout")}</NavLink>;
}

function getDefaultValue(list, selectedValue) {
  if (Array.isArray(list)) {
    const g = list.filter(l => l?.value === selectedValue)[0];
    return g?.value || "";
  }
  return "";
}
function GroupSelect({ list, selectDropDownRef, handleFocus, changeHandler, selectedValue }) {
  return (
    <label htmlFor="user-group">
      <span className="invisible">{text("header.roster_owner")}</span>
      <div ref={selectDropDownRef}>
        <select
          onFocus={handleFocus}
          style={{ color: "white" }}
          name="usergroup"
          id="user-group"
          value={getDefaultValue(list, selectedValue)}
          className="px-2 py-1 select-dropdown-indicator-header dropdown-menu"
          onChange={e => {
            const {
              target: { value }
            } = e;
            changeHandler(value);
          }}
        >
          {Array.isArray(list) &&
            list.map(d => (
              <option key={d.value} style={{ color: "black" }} value={d.value}>
                {d.label}
              </option>
            ))}
        </select>
      </div>
    </label>
  );
}
function UserGroupSelector({
  userAffiliations,
  canChangeGroup,
  isCTSUUser,
  rosterOwner,
  isCOGUser,
  selectedValue,
  selectDropDownRef,
  handleFocus,
  changeHandler
}) {
  const isAffiliatedToMultiRoster = userAffiliations.length > 1;
  if (selectedValue !== undefined) {
    return (
      <div
        style={{
          verticalAlign: "bottom",
          marginRight: ".5rem"
        }}
      >
        {(() => {
          if (canChangeGroup && isCTSUUser) {
            return (
              <GroupSelect list={rosterOwner} {...{ selectDropDownRef, handleFocus, changeHandler, selectedValue }} />
            );
          }
          if (isCOGUser) {
            return (
              <GroupSelect
                list={cogGroup.map(g => ({ label: g, value: g }))}
                {...{ selectDropDownRef, handleFocus, changeHandler, selectedValue }}
              />
            );
          }
          if (isAffiliatedToMultiRoster) {
            return (
              <GroupSelect
                list={userAffiliations.map(g => ({ label: g, value: g }))}
                {...{ selectDropDownRef, handleFocus, changeHandler, selectedValue }}
              />
            );
          }
          return <span className="invisible mx-3 px-3 py-1 border-all">{selectedValue}</span>;
        })()}
      </div>
    );
  }
  return null;
}
export default function RosteringHeader() {
  const context = useContext(AppContext);
  const { session, application } = context;
  const { userPermissions, pwdExpiryDays, username, impersonateLevel, userFirstLast, lastLogin, ctepId } = session ||
    [];
  const [modal, setModal] = useState(false);
  const dispatch = useDispatch();
  const query = GetDecodedQueryParams();
  const canChangeGroup = usePermission(csmKeys.ROSTERING_WELCOME_GROUP_EDIT);
  const selectDropDownRef = useRef();
  const userAffiliations = useUserAffliations();
  const userGroupFromParams = query.get("userGroup");
  const defaultUserGroup = useGetUserGroup();
  const userGroupRef = useRef(userGroupFromParams);
  const selectedValue = userGroupRef.current || defaultUserGroup;
  const navigate = useNavigate();
  const { data: rosterOwner } = useFetchRosterOwnerLovQuery();

  const isCOGUser = cogGroup.some(group => userAffiliations.includes(group));
  const isCTSUUser = userAffiliations.indexOf("CTSU") > -1;
  const adminPermission = useGetOneOfAdminPermissions();

  const dispatchUserGroup = useCallback(
    val => {
      userGroupRef.current = val;
      dispatch(updateUserGroup(val));
    },
    [dispatch]
  );

  const { dbName, appName } = application || [];

  function changeHandler(val) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const q = urlSearchParams.get("q");
    const queryParams = window.atob(q);
    const params = new URLSearchParams(queryParams);
    params.set("userGroup", val);
    const encodedParams = window.btoa(params.toString());
    const url = window.location.pathname + "?q=" + encodedParams;
    navigate(url, { replace: true });
    dispatchUserGroup(val);
  }

  useEffect(() => {
    if (ctepId) {
      dispatch(updateUserGroup(userGroupRef.current));
    }
  }, [dispatch, ctepId]);

  useCallback(() => {
    const setUserGroupFromParams = userGroupLov => {
      if (userGroupLov) {
        if (userGroupRef.current && userGroupLov.some(s => s.value === userGroupRef.current)) {
          dispatchUserGroup(userGroupRef.current);
          userGroupRef.current = null;
        } else {
          dispatchUserGroup(selectedValue);
        }
      }
    };
    if (userAffiliations.some(group => group === "CTSU")) {
      dispatchUserGroup("CTSU");
      setUserGroupFromParams(rosterOwner);
    } else if (isCOGUser) {
      const lovs = cogGroup.map(group => ({ label: group, value: group }));
      setUserGroupFromParams(lovs);
    } else {
      const usergroup = userAffiliations.sort().map(group => {
        return {
          label: group,
          value: group
        };
      });
      setUserGroupFromParams(usergroup);
    }
  }, [userAffiliations, dispatchUserGroup, isCOGUser, rosterOwner, selectedValue]);

  function handleFocus() {
    const isEditing = document.body.getAttribute("data-is-editing");
    if (isEditing) {
      setModal(true);
    }
  }

  const flagEditing = () => {
    document.body.removeAttribute("data-is-editing");
  };

  return (
    <>
      <Header
        key="header"
        appName={appName}
        displayAppName="Roster Maintenance"
        routes={appRoutes.map(route => {
          if (route.path === "/admin") {
            return {
              ...route,
              permission: adminPermission
            };
          }
          return route;
        })}
        logout={LogoutCmp}
        leftNavItems={() => (
          <>
            <UserGroupSelector
              {...{
                userAffiliations,
                canChangeGroup,
                isCTSUUser,
                rosterOwner,
                isCOGUser,
                selectedValue,
                selectDropDownRef,
                handleFocus,
                changeHandler
              }}
            />
          </>
        )}
        userPermissions={userPermissions}
        dbName={dbName}
        impersonateContext={context}
        userFirstLast={userFirstLast}
        coreVersion="v1"
        fnToken={getToken}
        username={username}
        impersonateLevel={impersonateLevel}
        lastLogin={lastLogin}
        ctepId={ctepId}
        pwdExpiryDays={pwdExpiryDays}
      />

      {modal && (
        <Modal
          id="Modal_Batch_Approval"
          modalTitle="Unsaved Changes"
          onClose={() => setModal(false)}
          showCloseBtn={false}
        >
          <div className="my-3">{text("header.alert_message")}</div>
          <div className="col-12 mt-3 row justify-content-center">
            <button
              onClick={() => {
                setModal(false);
                flagEditing();
              }}
              type="button"
              className="btn turquoise rounded px-3"
              aria-label="continue"
              data-id="Continue"
            >
              {text("common.continue")}
            </button>
            <button
              onClick={() => {
                setModal(false);
              }}
              type="button"
              className="ml-5 btn turquoise rounded px-3"
              aria-label="cancel"
              data-id="cancel"
            >
              {text("common.reset")}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
